import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import EraViewModal from "@/components/EraViewModal";
import ActionButton from "@/components/ActionButton";
import { getEobAnalysis, getEra } from "@/lib/eobEraV2";

// Full-page "View ERA" — same result presentation as the EOB → ERA flow, but reached
// by tapping a row on the Payments & ERA screen (no modal). Toolbar lives in the page
// header; the ERA body aligns to the page's horizontal padding.
export default function ViewEra() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filename = location.state?.filename || "Remittance";

  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [era, setEra] = useState({ available: false, text: "" });
  const [error, setError] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(false);
      const [an, er] = await Promise.all([
        getEobAnalysis(fileId).catch(() => null),
        getEra(fileId).catch(() => ({ available: false, text: "" })),
      ]);
      if (!alive) return;
      setAnalysis(an);
      setEra(er || { available: false, text: "" });
      setError(!an);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [fileId]);

  const download835 = () => {
    if (!era?.text) return;
    const blob = new Blob([era.text], { type: "application/edi-x12" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(filename || "eob").replace(/\.[^.]+$/, "")}.era`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="view-era-page">
      {/* Header */}
      <div className="w-full flex-shrink-0 mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[24px] font-bold tracking-[-0.7px] text-[#000] leading-none">View ERA</h1>
          <p className="text-[12px] text-[#6e6e73] mt-1.5 truncate max-w-[560px]">{filename}</p>
        </div>
        {analysis && !loading && !error ? (
          <div className="flex items-center gap-2 flex-shrink-0" data-testid="view-era-toolbar">
            <ActionButton onClick={download835} variant="solid" icon={Download} disabled={!era.text} data-testid="view-era-download-btn">
              Download ERA (835)
            </ActionButton>
            {era.text ? (
              <ActionButton onClick={() => setShowRaw((v) => !v)} data-testid="view-era-raw-btn">
                {showRaw ? "Hide 835" : "Raw 835"}
              </ActionButton>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex-1 min-h-[320px] flex flex-col items-center justify-center gap-3 text-[#6e6e73]">
          <Loader2 size={26} className="animate-spin text-[#1d1d1f]" />
          <div className="text-[12px]">Loading remittance…</div>
        </div>
      ) : error ? (
        <div className="flex-1 min-h-[280px] flex flex-col items-center justify-center gap-3 text-center px-6">
          <div className="text-[13px] font-semibold text-[#1d1d1f]">Couldn't load this remittance</div>
          <div className="text-[11px] text-[#6e6e73]">The analysis for this file isn't available yet.</div>
          <button onClick={() => navigate("/payments")} data-testid="view-era-close"
            className="text-[11px] font-semibold px-3 py-1.5 rounded-[9px] bg-[#1d1d1f] text-white">Back to Payments</button>
        </div>
      ) : (
        <div className="flex-1 min-h-0 w-full" style={{ zoom: 1.2 }}>
          <EraViewModal
            variant="records"
            hideToolbar
            showRaw={showRaw}
            onToggleRaw={() => setShowRaw((v) => !v)}
            analysis={analysis}
            eraText={era.text}
            filename={filename}
            originalFile={null}
            onDownload={download835}
            onChooseFlow={() => navigate("/payments")}
            onFinish={() => navigate("/payments")}
          />
        </div>
      )}
    </div>
  );
}
